import {
	EVENT_TYPES,
	PaymentCompleted,
	type ApplicationError,
	type EnvironmentReadyData,
	type EventMessage
} from 'core'
import { createState } from 'shared'

import {
	setSmartContractWorkspaceReadyState,
	smartContractsState
} from 'state/smart_contracts'

interface SystemNotificationState {
	onCloseCallback?: () => void
	loadingMessage?: string
	title?: string
	message?: string
	error?: ApplicationError
	acceptText?: string
	display?: 'dialog' | 'notification' | 'both' | 'loading' | 'none'
	variant?: 'error' | 'success' | 'info' | 'warning'
}

type Notification = {
	title: string
	time: string
	id: string
	description: string
	workspaceURL: string
	cto?: () => void
}

const systemNotificationState = createState<SystemNotificationState>({
	display: 'none',
	error: undefined
})
export const systemNotification = systemNotificationState.state

export const notificationsStateContainer = createState<Notification[]>([])
export const notificationsState = notificationsStateContainer.state

export const emitSystemNotification = ({
	onCloseCallback,
	loadingMessage,
	title,
	message,
	error,
	acceptText,
	display = 'none',
	variant = 'warning'
}: SystemNotificationState) => {
	systemNotificationState.updateState(() => ({
		onCloseCallback,
		loadingMessage,
		error,
		title,
		message,
		display,
		variant,
		acceptText
	}))
}

export const handleEnvironmentReadyEvent = (data: EnvironmentReadyData) => {
	if (smartContractsState.value.length === 0) return

	const smartContractReady = smartContractsState.value.find(
		smartContract => smartContract.webIdeUrl === data.workspaceURL
	)

	if (smartContractReady) {
		setSmartContractWorkspaceReadyState(smartContractReady.id, true)

		// Check if notification should be shown based on if the user is already in the smart contract workspace
		const showNotification = !(
			window.location.pathname.includes(smartContractReady.projectId) &&
			window.location.pathname.includes(smartContractReady.name)
		)

		// If user is not in the current workspace, show a notification
		if (showNotification) {
			notificationsStateContainer.updateState(notifications => [
				...notifications,
				{
					title: 'Workspace ready',
					description: `Workspace ${smartContractReady.name} is ready`,
					time: new Date(data.timestamp).toLocaleTimeString(),
					id: data.workspaceURL,
					workspaceURL: `/dapp/${smartContractReady.projectId}/smart-contract/${smartContractReady.name}/workspace`
				}
			])
		}
	}
}

export const clearAllNotifications = () => {
	notificationsStateContainer.updateState(() => [])
}

export const removeNotification = (notificationId: string) => {
	notificationsStateContainer.updateState(notifications =>
		notifications.filter(notification => notification.id !== notificationId)
	)
}

const handlePlanUpgradeEvent = async (event: PaymentCompleted) => {
	if (
		event.paymentStatus === 'failed' &&
		window.location.pathname !== '/teams/setup-members'
	) {
		emitSystemNotification({
			title: 'Payment failed',
			message: 'There was an error processing your payment',
			variant: 'error',
			display: 'notification'
		})
		return
	}

	if (
		event.paymentStatus === 'paid' &&
		window.location.pathname !== '/teams/setup-members'
	) {
		emitSystemNotification({
			title: 'Payment successful',
			message: 'Your payment has been processed successfully',
			variant: 'success',
			display: 'notification'
		})
	}

	// SUCCESS PAYMENT
	// FIXME: dependency cycle error

	// const currentSub = userInformationState.peek()?.subscription.id
	// if (currentSub !== event.subscription_id) {
	// await getUserInformation().catch(err => {
	// 	if (process.env.NODE_ENV === 'development') {
	// 		console.error('Error fetching user information:', err)
	// 	}
	// })
	// }
}

export const handleReceiveEvent = (event: EventMessage) => {
	switch (event.type) {
		case EVENT_TYPES.EnvironmentReady: {
			handleEnvironmentReadyEvent(event.data as EnvironmentReadyData)
			break
		}
		case EVENT_TYPES.PaymentCompleted: {
			handlePlanUpgradeEvent(event.data as PaymentCompleted)
			break
		}

		default:
			break
	}
}

export const resetSystemNotification = () => {
	systemNotificationState.updateState(() => ({
		display: 'none',
		variant: 'warning',
		title: undefined,
		message: undefined,
		error: undefined,
		loadingMessage: undefined,
		onCloseCallback: undefined
	}))
}
